import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { authService } from "@/features/auth/services/authService";
import { useLogout } from "@/features/auth/store";

const DEFAULT_TIMEOUT_MINUTES = 30;
const WARNING_BEFORE_MS = 60_000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"] as const;

export function IdleTimeoutGuard() {
  const logout = useLogout();
  const [timeoutMs, setTimeoutMs] = useState(
    DEFAULT_TIMEOUT_MINUTES * 60 * 1000,
  );
  const [warningOpen, setWarningOpen] = useState(false);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void authService.getCurrentUser().then((user) => {
      const minutes =
        (user as { sessionTimeoutMinutes?: number }).sessionTimeoutMinutes ??
        DEFAULT_TIMEOUT_MINUTES;
      if (minutes > 0) setTimeoutMs(minutes * 60 * 1000);
    });
  }, []);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    const idleUntilWarning = Math.max(timeoutMs - WARNING_BEFORE_MS, 0);
    warningTimerRef.current = setTimeout(() => {
      setWarningOpen(true);
      logoutTimerRef.current = setTimeout(() => {
        setWarningOpen(false);
        void logout();
      }, WARNING_BEFORE_MS);
    }, idleUntilWarning);
  }, [clearTimers, logout, timeoutMs]);

  const resetActivity = useCallback(() => {
    if (!warningOpen) scheduleTimers();
  }, [scheduleTimers, warningOpen]);

  useEffect(() => {
    scheduleTimers();
    const onActivity = () => resetActivity();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    return () => {
      clearTimers();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
    };
  }, [clearTimers, resetActivity, scheduleTimers]);

  function staySignedIn() {
    setWarningOpen(false);
    clearTimers();
    scheduleTimers();
  }

  return (
    <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
      <DialogContent dir="rtl" className="sm:max-w-md">
        <DialogHeader className="text-right">
          <DialogTitle>انتهاء الجلسة قريباً</DialogTitle>
          <DialogDescription>
            لم يُرصد أي نشاط. سيتم تسجيل خروجك تلقائياً خلال دقيقة واحدة ما لم
            تستمر في العمل.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-start">
          <Button onClick={staySignedIn}>البقاء متصلاً</Button>
          <Button variant="outline" onClick={() => void logout()}>
            تسجيل الخروج
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
